import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { workerService } from '../../services/workerService';
import { siteService } from '../../services/siteService';
import { assignmentService } from '../../services/assignmentService';
import { companyService } from '../../services/companyService';
import { authService } from '../../services/authService';
import { useLanguage } from '../../contexts/LanguageContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';

const AdminWorkerAssignment = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const [workers, setWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [companyId, setCompanyId] = useState(null);

  useEffect(() => {
    if (userProfile?.role !== 'admin') {
      navigate('/home', { replace: true });
      return;
    }
    loadData();
  }, [userProfile, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const companyResult = await companyService?.getUserCompany();
      if (companyResult?.error) throw companyResult?.error;
      setCompanyId(companyResult?.data?.id);

      const [workersResult, sitesResult, assignmentsResult] = await Promise.all([
        workerService?.getAllWorkers(),
        siteService?.getSelectableSites(),
        assignmentService?.getAllTodayAssignments()
      ]);

      if (workersResult?.error) throw workersResult?.error;
      if (sitesResult?.error) throw sitesResult?.error;

      setWorkers(workersResult?.data || []);
      setSites(sitesResult?.data || []);

      const assignmentMap = {};
      (assignmentsResult?.data || [])?.forEach(assignment => {
        assignmentMap[assignment?.user_id] = assignment?.site_id;
      });
      setAssignments(assignmentMap);
    } catch (err) {
      console.error('[AdminWorkerAssignment] load data failed:', err);
      setError('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (workerId, newRole) => {
    if (subscriptionReadOnly) return;
    try {
      setError('');
      const result = await authService?.updateUserProfile(workerId, { role: newRole });
      if (result?.error) throw result?.error;

      setWorkers(prev => prev?.map(w => w?.id === workerId ? { ...w, role: newRole } : w));
      setSuccessMessage('Role updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('[AdminWorkerAssignment] update role failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Permission denied updating role: ${err?.message}`);
      } else {
        setError(`Failed to update role: ${err?.message || 'Unknown error'}`);
      }
    }
  };

  const handleSiteAssignment = async (workerId, siteId) => {
    if (subscriptionReadOnly) return;
    try {
      setError('');
      const today = new Date()?.toISOString()?.split('T')?.[0];

      if (siteId) {
        const result = await assignmentService?.setWorkerAssignment(
          workerId,
          siteId,
          companyId,
          today
        );
        if (result?.error) throw result?.error;
      } else {
        const result = await assignmentService?.removeWorkerAssignment(workerId, today);
        if (result?.error) throw result?.error;
      }

      setAssignments(prev => ({ ...prev, [workerId]: siteId || null }));
      setSuccessMessage('Assignment updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('[AdminWorkerAssignment] update assignment failed:', err);
      setError('Failed to update assignment. Please try again.');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-destructive/10 text-destructive';
      case 'site_manager':
        return 'bg-purple-100 text-purple-700';
      case 'worker':
        return 'bg-accent/10 text-accent';
      case 'client':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'site_manager', label: 'Site Manager' },
    { value: 'worker', label: 'Worker' },
    { value: 'client', label: 'Client' }
  ];

  const siteOptions = [
    { value: '', label: 'No Assignment' },
    ...sites?.filter(s => s?.status === 'active')?.map(site => ({
      value: site?.id,
      label: site?.site_name
    }))
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Worker Assignments - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <SubscriptionReadOnlyBanner className="mb-6" />

            <div className="mb-8">
              <Button
                onClick={() => navigate('/admin-dashboard')}
                variant="ghost"
                className="mb-4"
              >
                <Icon name="ArrowLeft" size={18} />
                <span>{t('backToDashboard')}</span>
              </Button>
              <h1 className="text-3xl font-bold text-foreground mb-2">{t('workerAssignments')}</h1>
              <p className="text-muted-foreground">{t('workerAssignmentsSubtitle')}</p>
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {successMessage && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6 flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                <p className="text-sm text-success">{successMessage}</p>
              </div>
            )}

            <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">{t('name')}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">{t('email')}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">{t('role')}</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">{t('assignedSiteToday')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {workers?.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-12 text-center">
                          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-3" />
                          <p className="text-muted-foreground">{t('noWorkersFound')}</p>
                        </td>
                      </tr>
                    ) : (
                      workers?.map(worker => (
                        <tr key={worker?.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                                <Icon name="User" size={20} className="text-accent" />
                              </div>
                              <span className="font-medium text-foreground">{worker?.full_name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-muted-foreground">{worker?.email}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="w-48">
                              <Select
                                value={worker?.role}
                                options={roleOptions}
                                onChange={(value) => handleRoleChange(worker?.id, value)}
                                disabled={subscriptionReadOnly}
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="w-64">
                              <Select
                                value={assignments?.[worker?.id] || ''}
                                options={siteOptions}
                                onChange={(value) => handleSiteAssignment(worker?.id, value)}
                                placeholder="Select site"
                                disabled={subscriptionReadOnly}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 bg-muted/30 border border-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={18} className="text-accent mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">{t('assignmentNotes')}</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>{t('assignmentNote1')}</li>
                    <li>{t('assignmentNote2')}</li>
                    <li>{t('assignmentNote3')}</li>
                    <li>{t('assignmentNote4')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminWorkerAssignment;